import {
  StructureSoudures,
  Soudure,
  TypeSoudure,
  Point3D,
  DonneesTonnelle,
  ParametresSoudures
} from '../types';

/**
 * Service de calcul automatique et réactif des soudures
 * Recalcule toutes les soudures à chaque changement de la tonnelle
 */
export class SouduresCalculService {
  private toleranceDistance: number = 0.05; // 5cm de tolérance pour détecter les connexions

  /**
   * Calcule toutes les soudures de la tonnelle de manière réactive
   * Calcul correct pour structures 3D : montant-poutre + coins
   */
  public calculerToutesSoudures(
    donneesTonnelle: DonneesTonnelle,
    parametres: ParametresSoudures
  ): StructureSoudures {
    const souduresTemp: Soudure[] = [];
    let compteurId = 1;

    // 1. Soudures Montant-Poutre (structure de base) - HAUT ET BAS
    const souduresMontantPoutre = this.calculerSouduresMontantPoutreCorrigees(
      donneesTonnelle.structureBase,
      parametres,
      compteurId
    );
    souduresTemp.push(...souduresMontantPoutre);
    compteurId += souduresMontantPoutre.length;

    // 2. Soudures Poutre-Poutre (coins de la structure)
    const souduresPoutrePoutre = this.calculerSouduresPoutrePoutre(
      donneesTonnelle.structureBase,
      parametres,
      compteurId
    );
    souduresTemp.push(...souduresPoutrePoutre);
    compteurId += souduresPoutrePoutre.length;

    // 3. Soudures Traverse-Montant (si traverses présentes)
    if (donneesTonnelle.traverses && parametres.afficherTraverseMontant) {
      const souduresTraverseMontant = this.calculerSouduresTraverseMontant(
        donneesTonnelle.structureBase,
        donneesTonnelle.traverses,
        parametres,
        compteurId
      );
      souduresTemp.push(...souduresTraverseMontant);
      compteurId += souduresTraverseMontant.length;
    }

    // 4. Soudures Traverse-Traverse (croisements)
    if (donneesTonnelle.traverses && parametres.afficherTraverseTraverse) {
      const souduresTraverseTraverse = this.calculerSouduresTraverseTraverse(
        donneesTonnelle.traverses,
        parametres,
        compteurId
      );
      souduresTemp.push(...souduresTraverseTraverse);
      compteurId += souduresTraverseTraverse.length;
    }

    // 5. Soudures Double Poutre
    if (donneesTonnelle.doublePoutre && parametres.afficherDoublePoutre) {
      const souduresDoublePoutre = this.calculerSouduresDoublePoutre(
        donneesTonnelle.doublePoutre,
        parametres,
        compteurId
      );
      souduresTemp.push(...souduresDoublePoutre);
      compteurId += souduresDoublePoutre.length;
    }

    // Regroupement final pour éviter les doublons
    const soudures = this.regrouperSouduresParPosition(souduresTemp, parametres);

    // Calculer les statistiques
    const statistiques = this.calculerStatistiques(soudures);

    const nombreMontants = donneesTonnelle.structureBase.montants.length;
    const nombrePointsPoutre = donneesTonnelle.structureBase.poutres.poutreHaute.points.length;


    return {
      id: `soudures_${Date.now()}`,
      parametres,
      soudures,
      statistiques
    };
  }

  /**
   * Calcule les soudures entre montants et poutres
   * Une soudure par connexion montant-poutre (haut et bas)
   */
  private calculerSouduresMontantPoutre(
    structureBase: DonneesTonnelle['structureBase'],
    parametres: ParametresSoudures,
    startId: number
  ): Soudure[] {
    if (!parametres.afficherMontantPoutre) return [];

    const soudures: Soudure[] = [];
    let id = startId;

    structureBase.montants.forEach(montant => {
      // Soudure avec poutre haute - position au niveau du montant
      soudures.push({
        id: `soudure_${id++}`,
        type: TypeSoudure.MONTANT_POUTRE,
        position: {
          x: montant.position.x,
          y: structureBase.poutres.poutreHaute.points[0]?.y || (montant.position.y + 3),
          z: montant.position.z
        },
        taille: 0.02,
        couleur: parametres.couleur,
        elementA: montant.id,
        elementB: 'poutre_haute',
        visible: true
      });

      // Soudure avec poutre basse - position au niveau du montant
      soudures.push({
        id: `soudure_${id++}`,
        type: TypeSoudure.MONTANT_POUTRE,
        position: {
          x: montant.position.x,
          y: structureBase.poutres.poutreBasse.points[0]?.y || 0,
          z: montant.position.z
        },
        taille: parametres.taille * parametres.tailleMontantPoutre,
        couleur: parametres.couleur,
        elementA: montant.id,
        elementB: 'poutre_basse',
        visible: parametres.afficherMontantPoutre
      });
    });

    return soudures;
  }

  /**
   * Calcule les soudures aux coins (poutre-poutre)
   * SEULEMENT aux positions des montants pour les formes rondes/ovales
   */
  private calculerSouduresPoutrePoutre(
    structureBase: DonneesTonnelle['structureBase'],
    parametres: ParametresSoudures,
    startId: number
  ): Soudure[] {
    if (!parametres.afficherPoutrePoutre) return [];

    const soudures: Soudure[] = [];
    let id = startId;
    const montants = structureBase.montants;

    // Pour les formes rondes/ovales : soudures poutre-poutre AUX POSITIONS DES MONTANTS
    // Cela représente la connexion entre poutre haute et poutre basse à chaque montant
    montants.forEach(montant => {
      // Soudure poutre-poutre à la position du montant (poutre haute)
      soudures.push({
        id: `soudure_${id++}`,
        type: TypeSoudure.POUTRE_POUTRE,
        position: {
          x: montant.position.x,
          y: montant.position.y + montant.hauteur, // Hauteur de la poutre haute
          z: montant.position.z
        },
        taille: parametres.taille * parametres.taillePoutrePoutre,
        couleur: parametres.couleur,
        elementA: 'poutre_haute',
        elementB: 'poutre_basse',
        visible: true
      });

      // Soudure poutre-poutre à la position du montant (poutre basse)
      soudures.push({
        id: `soudure_${id++}`,
        type: TypeSoudure.POUTRE_POUTRE,
        position: {
          x: montant.position.x,
          y: montant.position.y, // Hauteur de la poutre basse
          z: montant.position.z
        },
        taille: parametres.taille * parametres.taillePoutrePoutre,
        couleur: parametres.couleur,
        elementA: 'poutre_haute',
        elementB: 'poutre_basse',
        visible: true
      });
    });

    return soudures;
  }

  /**
   * Calcule les soudures montant-poutre CORRECTEMENT
   * 2 soudures par montant : une en haut (poutre haute) et une en bas (poutre basse)
   * Corrige le calcul de hauteur pour les soudures hautes
   */
  private calculerSouduresMontantPoutreCorrigees(
    structureBase: {
      montants: { id: string; position: Point3D; hauteur: number; }[];
      poutres: {
        poutreHaute: { points: Point3D[]; };
        poutreBasse: { points: Point3D[]; };
      };
    },
    parametres: ParametresSoudures,
    id: number
  ): Soudure[] {
    const soudures: Soudure[] = [];
    let compteur = id;

    structureBase.montants.forEach(montant => {
      // LOGIQUE CORRECTE : Les soudures sont aux extrémités du montant
      // Soudure haute : en haut du montant (position.y + hauteur)
      // Soudure basse : en bas du montant (position.y)
      const hauteurMontant = montant.hauteur; // Utilise la hauteur réelle du montant
      
      // SOUDURE MONTANT - POUTRE HAUTE (en haut du montant)
      soudures.push({
        id: `soudure_${compteur++}`,
        type: TypeSoudure.MONTANT_POUTRE,
        position: {
          x: montant.position.x,
          y: montant.position.y + hauteurMontant, // En haut du montant
          z: montant.position.z
        },
        taille: parametres.taille * parametres.tailleMontantPoutre,
        couleur: parametres.couleur,
        elementA: montant.id,
        elementB: 'poutre_haute',
        visible: parametres.afficherMontantPoutre
      });

      // SOUDURE MONTANT - POUTRE BASSE (en bas du montant)
      soudures.push({
        id: `soudure_${compteur++}`,
        type: TypeSoudure.MONTANT_POUTRE,
        position: {
          x: montant.position.x,
          y: montant.position.y, // En bas du montant
          z: montant.position.z
        },
        taille: parametres.taille * parametres.tailleMontantPoutre,
        couleur: parametres.couleur,
        elementA: montant.id,
        elementB: 'poutre_basse',
        visible: parametres.afficherMontantPoutre
      });
    });
    
    return soudures;
  }

  /**
   * MÉTHODES LEGACY - Conservées pour compatibilité mais non utilisées
   * avec la nouvelle approche optimisée pour les formes 3D
   */
  
  /**
   * Regroupe les soudures par position pour éviter les doublons (LEGACY)
   */
  private regrouperSouduresParPosition(
    souduresTemp: Soudure[],
    parametres: ParametresSoudures
  ): Soudure[] {
    const souduresGroupees: Soudure[] = [];
    const positionsTraitees: Set<string> = new Set();

    souduresTemp.forEach(soudure => {
      const cle = this.creerClePosition(soudure.position);
      
      if (!positionsTraitees.has(cle)) {
        positionsTraitees.add(cle);
        const souduresMemePosition = souduresTemp.filter(s => 
          this.creerClePosition(s.position) === cle
        );
        
        const soudureConsolidee: Soudure = {
          ...soudure,
          id: `soudure_consolidee_${souduresGroupees.length + 1}`,
          type: this.determinerTypePrioritaire(souduresMemePosition),
          elementA: souduresMemePosition.map(s => s.elementA).join('+'),
          elementB: souduresMemePosition.map(s => s.elementB).join('+'),
          taille: Math.max(...souduresMemePosition.map(s => s.taille))
        };
        
        souduresGroupees.push(soudureConsolidee);
      }
    });

    return souduresGroupees;
  }

  /**
   * Crée une clé unique pour une position (arrondie) (LEGACY)
   */
  private creerClePosition(position: Point3D): string {
    const precision = 100;
    const x = Math.round(position.x * precision) / precision;
    const y = Math.round(position.y * precision) / precision;
    const z = Math.round(position.z * precision) / precision;
    return `${x}_${y}_${z}`;
  }

  /**
   * Détermine le type de soudure prioritaire (LEGACY)
   */
  private determinerTypePrioritaire(soudures: Soudure[]): TypeSoudure {
    const priorites = {
      [TypeSoudure.MONTANT_POUTRE]: 1,
      [TypeSoudure.POUTRE_POUTRE]: 2,
      [TypeSoudure.TRAVERSE_MONTANT]: 3,
      [TypeSoudure.TRAVERSE_TRAVERSE]: 4,
      [TypeSoudure.DOUBLE_POUTRE]: 5,
      [TypeSoudure.ACCESSOIRE]: 6
    };
    
    return soudures.reduce((typePrioritaire, soudure) => {
      return priorites[soudure.type] < priorites[typePrioritaire] ? soudure.type : typePrioritaire;
    }, soudures[0].type);
  }

  /**
   * Calcule les soudures entre traverses et montants
   * CORRECTION: Amélioration pour les motifs personnalisés
   */
  private calculerSouduresTraverseMontant(
    structureBase: DonneesTonnelle['structureBase'],
    traverses: DonneesTonnelle['traverses'],
    parametres: ParametresSoudures,
    startId: number
  ): Soudure[] {
    const soudures: Soudure[] = [];
    let id = startId;

    if (!traverses) return soudures;



    traverses.rangees.forEach(rangee => {
      rangee.traverses.forEach(traverse => {
        // OPTIMISATION: Détection du type de traverse pour optimisations spécifiques
        const estTraversePersonnalisee = traverse.id.includes('personnalise');
        const estTraverseSimple = traverse.id.includes('simple') || traverse.id.includes('vague') || traverse.id.includes('losange');
        
        if (estTraverseSimple) {
          // OPTIMISATION AGRESSIVE pour traverses simples/vague/losange

          
          const debut = traverse.points[0];
          const fin = traverse.points[traverse.points.length - 1];

          // Calcul optimisé avec cache des montants proches
          const montantsProches = this.filtrerMontantsProches(
            structureBase.montants, 
            debut, 
            fin, 
            this.toleranceDistance * 2 // Tolérance élargie pour performance
          );

          // Soudure au début - recherche dans montants filtrés
          const montantDebut = this.trouverMontantPlusProcheOptimise(debut, montantsProches);
          if (montantDebut) {
            soudures.push({
              id: `soudure_${id++}`,
              type: TypeSoudure.TRAVERSE_MONTANT,
              position: {
                x: montantDebut.position.x,
                y: debut.y,
                z: montantDebut.position.z
              },
              taille: parametres.taille * parametres.tailleTraverseMontant,
              couleur: parametres.couleur,
              elementA: traverse.id,
              elementB: montantDebut.id,
              visible: true
            });
          }

          // Soudure à la fin - recherche dans montants filtrés
          const montantFin = this.trouverMontantPlusProcheOptimise(fin, montantsProches);
          if (montantFin && montantFin.id !== montantDebut?.id) {
            soudures.push({
              id: `soudure_${id++}`,
              type: TypeSoudure.TRAVERSE_MONTANT,
              position: {
                x: montantFin.position.x,
                y: fin.y,
                z: montantFin.position.z
              },
              taille: parametres.taille * parametres.tailleTraverseMontant,
              couleur: parametres.couleur,
              elementA: traverse.id,
              elementB: montantFin.id,
              visible: true
            });
          }
        } else if (estTraversePersonnalisee) {
          // CORRECTION: Pour les motifs personnalisés, utiliser UNIQUEMENT les vrais points de contact

          
          const pointsContact = this.calculerPointsContactReels(traverse, structureBase.montants);
          
          pointsContact.forEach(contact => {

            soudures.push({
              id: `soudure_${id++}`,
              type: TypeSoudure.TRAVERSE_MONTANT,
              position: contact.pointContact,
              taille: parametres.taille * parametres.tailleTraverseMontant,
              couleur: parametres.couleur,
              elementA: traverse.id,
              elementB: contact.montant.id,
              visible: true
            });
          });
        } else {
          // Pour les autres traverses, utiliser l'ancienne logique

          
          const debut = traverse.points[0];
          const fin = traverse.points[traverse.points.length - 1];

          // Soudure au début de la traverse
          const montantDebut = this.trouverMontantPlusProcheAmeliore(debut, structureBase.montants);
          if (montantDebut) {

            soudures.push({
              id: `soudure_${id++}`,
              type: TypeSoudure.TRAVERSE_MONTANT,
              position: {
                x: montantDebut.position.x,
                y: debut.y,
                z: montantDebut.position.z
              },
              taille: parametres.taille * parametres.tailleTraverseMontant,
              couleur: parametres.couleur,
              elementA: traverse.id,
              elementB: montantDebut.id,
              visible: true
            });
          } else {

          }

          // Soudure à la fin de la traverse
          const montantFin = this.trouverMontantPlusProcheAmeliore(fin, structureBase.montants);
          if (montantFin && montantFin.id !== montantDebut?.id) {

            soudures.push({
              id: `soudure_${id++}`,
              type: TypeSoudure.TRAVERSE_MONTANT,
              position: {
                x: montantFin.position.x,
                y: fin.y,
                z: montantFin.position.z
              },
              taille: parametres.taille * parametres.tailleTraverseMontant,
              couleur: parametres.couleur,
              elementA: traverse.id,
              elementB: montantFin.id,
              visible: true
            });
          } else if (!montantFin) {

          }
        }
      });
    });


    return soudures;
  }

  /**
   * Calcule les soudures aux croisements de traverses - VERSION OPTIMISÉE
   */
  private calculerSouduresTraverseTraverse(
    traverses: DonneesTonnelle['traverses'],
    parametres: ParametresSoudures,
    startId: number
  ): Soudure[] {
    const soudures: Soudure[] = [];
    let id = startId;

    if (!traverses || traverses.rangees.length < 2) return soudures;



    // OPTIMISATION : Limiter le nombre de croisements calculés
    let croisementsCalcules = 0;
    const MAX_CROISEMENTS = 20; // Limite pour éviter le blocage

    // Détecter les croisements entre traverses de différentes rangées
    for (let i = 0; i < traverses.rangees.length && croisementsCalcules < MAX_CROISEMENTS; i++) {
      for (let j = i + 1; j < traverses.rangees.length && croisementsCalcules < MAX_CROISEMENTS; j++) {
        const rangee1 = traverses.rangees[i];
        const rangee2 = traverses.rangees[j];

        // OPTIMISATION : Vérification rapide de hauteur
        if (Math.abs(rangee1.hauteur - rangee2.hauteur) > this.toleranceDistance) {
          continue; // Pas de croisement possible si hauteurs trop différentes
        }

        for (const traverse1 of rangee1.traverses) {
          if (croisementsCalcules >= MAX_CROISEMENTS) break;
          
          // OPTIMISATION: Détection du type de traverse pour optimisations spécifiques
          const estTraverse1Simple = traverse1.id.includes('simple') || traverse1.id.includes('vague') || traverse1.id.includes('losange');
          
          for (const traverse2 of rangee2.traverses) {
            if (croisementsCalcules >= MAX_CROISEMENTS) break;
            
            const estTraverse2Simple = traverse2.id.includes('simple') || traverse2.id.includes('vague') || traverse2.id.includes('losange');
            
            croisementsCalcules++;
            
            // OPTIMISATION AGRESSIVE pour traverses simples/vague/losange
            if (estTraverse1Simple && estTraverse2Simple) {
              const croisements = this.detecterCroisementsOptimise(traverse1, traverse2);
              
              croisements.forEach(croisement => {
                soudures.push({
                  id: `soudure_${id++}`,
                  type: TypeSoudure.TRAVERSE_TRAVERSE,
                  position: croisement,
                  taille: parametres.taille * parametres.tailleTraverseTraverse,
                  couleur: parametres.couleur,
                  elementA: traverse1.id,
                  elementB: traverse2.id,
                  visible: true
                });
              });
            } else {
              // Méthode normale pour traverses personnalisées ou mixtes
              const croisements = this.detecterCroisements(traverse1, traverse2);
              
              croisements.forEach(croisement => {
                soudures.push({
                  id: `soudure_${id++}`,
                  type: TypeSoudure.TRAVERSE_TRAVERSE,
                  position: croisement,
                  taille: parametres.taille * parametres.tailleTraverseTraverse,
                  couleur: parametres.couleur,
                  elementA: traverse1.id,
                  elementB: traverse2.id,
                  visible: true
                });
              });
            }
          }
        }
      }
    }


    return soudures;
  }

  // Fonction trouverPointPlusProche supprimée car logique incorrecte
  // Les soudures montant-poutre doivent être aux extrémités du montant,
  // pas à la hauteur d'un point de poutre aléatoire

  /**
   * Trouve le montant le plus proche d'un point
   */
  private trouverMontantPlusProche(
    point: Point3D,
    montants: Array<{ id: string; position: Point3D }>
  ): { id: string; position: Point3D } | null {
    let montantPlusProche: { id: string; position: Point3D } | null = null;
    let distanceMin = Infinity;

    montants.forEach(montant => {
      const distance = this.calculerDistance2D(point, montant.position);
      if (distance < distanceMin && distance <= this.toleranceDistance) {
        distanceMin = distance;
        montantPlusProche = montant;
      }
    });

    return montantPlusProche;
  }

  /**
   * Version améliorée pour les motifs personnalisés
   * Utilise une tolérance plus large et une logique adaptée
   */
  private trouverMontantPlusProcheAmeliore(
    point: Point3D,
    montants: Array<{ id: string; position: Point3D }>
  ): { id: string; position: Point3D } | null {
    let montantPlusProche: { id: string; position: Point3D } | null = null;
    let distanceMin = Infinity;
    
    // Tolérance plus large pour les motifs personnalisés (20cm au lieu de 5cm)
    const toleranceAmeliore = 0.20;

    montants.forEach(montant => {
      const distance = this.calculerDistance2D(point, montant.position);
      
      if (distance < distanceMin && distance <= toleranceAmeliore) {
        distanceMin = distance;
        montantPlusProche = montant;
      }
    });

    return montantPlusProche;
  }

  /**
   * Version optimisée pour traverses simples/vague/losange
   * Recherche dans une liste pré-filtrée de montants proches
   */
  private trouverMontantPlusProcheOptimise(
    point: Point3D,
    montantsProches: Array<{ id: string; position: Point3D }>
  ): { id: string; position: Point3D } | null {
    if (montantsProches.length === 0) return null;
    
    let montantPlusProche: { id: string; position: Point3D } | null = null;
    let distanceMin = Infinity;

    // Recherche optimisée dans la liste pré-filtrée
    for (const montant of montantsProches) {
      // Calcul de distance 2D rapide
      const dx = point.x - montant.position.x;
      const dz = point.z - montant.position.z;
      const distance = dx * dx + dz * dz; // Distance au carré pour éviter sqrt

      if (distance < distanceMin) {
        distanceMin = distance;
        montantPlusProche = montant;
      }
    }

    // Vérification finale avec la vraie distance
    if (montantPlusProche && Math.sqrt(distanceMin) < this.toleranceDistance * 2) {
      return montantPlusProche;
    }

    return null;
  }

  /**
   * Calcule les vrais points de contact entre une traverse personnalisée et les montants
   * Analyse les segments de la traverse pour trouver les intersections réelles avec les montants
   * Retourne UN SEUL point de contact par montant (le plus proche)
   */
  private calculerPointsContactReels(
    traverse: { id: string; points: Point3D[] },
    montants: Array<{ id: string; position: Point3D }>
  ): Array<{ montant: { id: string; position: Point3D }; pointContact: Point3D }> {
    const rayonMontant = 0.025; // Rayon du montant (2.5cm)
    const toleranceDetection = 0.15; // 15cm de tolérance pour détecter les intersections
    
    // OPTIMISATION CRITIQUE : Limiter drastiquement le nombre de points analysés
    const MAX_POINTS_ANALYSE = 10; // Maximum 10 points pour éviter le blocage
    const pointsEchantillonnes = this.echantillonnerPoints(traverse.points, MAX_POINTS_ANALYSE);
    
    // Map pour stocker le meilleur contact par montant
    const meilleursContacts = new Map<string, { montant: { id: string; position: Point3D }; pointContact: Point3D; distance: number }>();



    // Analyser seulement les segments échantillonnés
    for (let i = 0; i < pointsEchantillonnes.length - 1; i++) {
      const pointA = pointsEchantillonnes[i];
      const pointB = pointsEchantillonnes[i + 1];
      
      // PRÉ-FILTRAGE : Seulement les montants proches de ce segment
      const montantsProches = this.filtrerMontantsProches(montants, pointA, pointB, toleranceDetection * 2);
      
      montantsProches.forEach(montant => {
        // Calculer la distance du montant au segment AB
        const distanceAuSegment = this.calculerDistancePointSegment2D(montant.position, pointA, pointB);
        
        if (distanceAuSegment <= rayonMontant + toleranceDetection) {
          // Trouver le point le plus proche sur le segment
          const pointPlusProche = this.projetterPointSurSegment2D(montant.position, pointA, pointB);
          
          // Placer la soudure à l'axe du montant (centre)
          const pointContact: Point3D = {
            x: montant.position.x,  // Centre X du montant
            y: pointPlusProche.y,   // Hauteur Y de la traverse
            z: montant.position.z   // Centre Z du montant
          };
          
          // Garder seulement le contact le plus proche pour chaque montant
          const contactExistant = meilleursContacts.get(montant.id);
          if (!contactExistant || distanceAuSegment < contactExistant.distance) {
            meilleursContacts.set(montant.id, {
              montant,
              pointContact,
              distance: distanceAuSegment
            });
          }
        }
      });
    }

    // Convertir la Map en Array
    const pointsContact = Array.from(meilleursContacts.values()).map(contact => ({
      montant: contact.montant,
      pointContact: contact.pointContact
    }));


    return pointsContact;
  }

  /**
   * Échantillonne intelligemment les points d'une traverse pour réduire la complexité
   */
  private echantillonnerPoints(points: Point3D[], maxPoints: number): Point3D[] {
    if (points.length <= maxPoints) {
      return points;
    }
    
    const echantillonnes: Point3D[] = [];
    const step = (points.length - 1) / (maxPoints - 1);
    
    for (let i = 0; i < maxPoints; i++) {
      const index = Math.round(i * step);
      echantillonnes.push(points[index]);
    }
    
    return echantillonnes;
  }

  /**
   * Pré-filtre les montants proches d'un segment pour éviter les calculs inutiles
   */
  private filtrerMontantsProches(
    montants: Array<{ id: string; position: Point3D }>,
    pointA: Point3D,
    pointB: Point3D,
    tolerance: number
  ): Array<{ id: string; position: Point3D }> {
    const minX = Math.min(pointA.x, pointB.x) - tolerance;
    const maxX = Math.max(pointA.x, pointB.x) + tolerance;
    const minZ = Math.min(pointA.z, pointB.z) - tolerance;
    const maxZ = Math.max(pointA.z, pointB.z) + tolerance;
    
    return montants.filter(montant => 
      montant.position.x >= minX && montant.position.x <= maxX &&
      montant.position.z >= minZ && montant.position.z <= maxZ
    );
  }

  /**
   * Calcule la distance d'un point à un segment de ligne en 2D (plan XZ)
   */
  private calculerDistancePointSegment2D(point: Point3D, segmentA: Point3D, segmentB: Point3D): number {
    const A = { x: segmentA.x, z: segmentA.z };
    const B = { x: segmentB.x, z: segmentB.z };
    const P = { x: point.x, z: point.z };
    
    const AB = { x: B.x - A.x, z: B.z - A.z };
    const AP = { x: P.x - A.x, z: P.z - A.z };
    
    const AB_AB = AB.x * AB.x + AB.z * AB.z;
    
    if (AB_AB === 0) {
      // Segment de longueur nulle
      return Math.sqrt((P.x - A.x) * (P.x - A.x) + (P.z - A.z) * (P.z - A.z));
    }
    
    const t = Math.max(0, Math.min(1, (AP.x * AB.x + AP.z * AB.z) / AB_AB));
    const projection = { x: A.x + t * AB.x, z: A.z + t * AB.z };
    
    return Math.sqrt((P.x - projection.x) * (P.x - projection.x) + (P.z - projection.z) * (P.z - projection.z));
  }

  /**
   * Projette un point sur un segment de ligne en 2D et retourne le point 3D correspondant
   */
  private projetterPointSurSegment2D(point: Point3D, segmentA: Point3D, segmentB: Point3D): Point3D {
    const A = { x: segmentA.x, z: segmentA.z };
    const B = { x: segmentB.x, z: segmentB.z };
    const P = { x: point.x, z: point.z };
    
    const AB = { x: B.x - A.x, z: B.z - A.z };
    const AP = { x: P.x - A.x, z: P.z - A.z };
    
    const AB_AB = AB.x * AB.x + AB.z * AB.z;
    
    if (AB_AB === 0) {
      // Segment de longueur nulle
      return segmentA;
    }
    
    const t = Math.max(0, Math.min(1, (AP.x * AB.x + AP.z * AB.z) / AB_AB));
    
    return {
      x: segmentA.x + t * (segmentB.x - segmentA.x),
      y: segmentA.y + t * (segmentB.y - segmentA.y),
      z: segmentA.z + t * (segmentB.z - segmentA.z)
    };
  }

  /**
   * Détecte les croisements entre deux traverses - VERSION ULTRA-OPTIMISÉE
   */
  private detecterCroisements(
    traverse1: { points: Point3D[] },
    traverse2: { points: Point3D[] }
  ): Point3D[] {
    const croisements: Point3D[] = [];

    // OPTIMISATION CRITIQUE : Échantillonnage drastique pour éviter le blocage
    const MAX_POINTS_CROISEMENT = 5; // Maximum 5 points par traverse pour les croisements
    const points1 = this.echantillonnerPoints(traverse1.points, MAX_POINTS_CROISEMENT);
    const points2 = this.echantillonnerPoints(traverse2.points, MAX_POINTS_CROISEMENT);
    
    // Vérification rapide des bounding boxes pour éviter les calculs inutiles
    if (!this.boundingBoxesSeRecoupent(points1, points2)) {
      return croisements; // Pas de croisement possible
    }

    // Analyse seulement des points échantillonnés
    for (const p1 of points1) {
      for (const p2 of points2) {
        if (Math.abs(p1.y - p2.y) < this.toleranceDistance) {
          const distance2D = this.calculerDistance2D(p1, p2);
          if (distance2D < this.toleranceDistance) {
            croisements.push({
              x: (p1.x + p2.x) / 2,
              y: (p1.y + p2.y) / 2,
              z: (p1.z + p2.z) / 2
            });
            // COURT-CIRCUIT : Un seul croisement suffit pour les performances
            return croisements;
          }
        }
      }
    }

    return croisements;
  }

  /**
   * Version ultra-optimisée pour traverses simples/vague/losange
   * Utilise seulement les points de début et fin pour détecter les croisements
   */
  private detecterCroisementsOptimise(
    traverse1: { points: Point3D[] },
    traverse2: { points: Point3D[] }
  ): Point3D[] {
    const croisements: Point3D[] = [];

    // OPTIMISATION ULTRA-AGRESSIVE : Seulement début et fin de chaque traverse
    const points1 = [
      traverse1.points[0], 
      traverse1.points[Math.floor(traverse1.points.length / 2)], // Point milieu
      traverse1.points[traverse1.points.length - 1]
    ];
    const points2 = [
      traverse2.points[0], 
      traverse2.points[Math.floor(traverse2.points.length / 2)], // Point milieu
      traverse2.points[traverse2.points.length - 1]
    ];
    
    // Vérification rapide des bounding boxes
    if (!this.boundingBoxesSeRecoupent(points1, points2)) {
      return croisements;
    }

    // Analyse ultra-rapide avec tolérance élargie
    const toleranceElargie = this.toleranceDistance * 1.5;
    
    for (const p1 of points1) {
      for (const p2 of points2) {
        if (Math.abs(p1.y - p2.y) < toleranceElargie) {
          // Calcul de distance rapide (au carré pour éviter sqrt)
          const dx = p1.x - p2.x;
          const dz = p1.z - p2.z;
          const distance2DCarree = dx * dx + dz * dz;
          
          if (distance2DCarree < toleranceElargie * toleranceElargie) {
            croisements.push({
              x: (p1.x + p2.x) / 2,
              y: (p1.y + p2.y) / 2,
              z: (p1.z + p2.z) / 2
            });
            // COURT-CIRCUIT IMMÉDIAT : Un croisement suffit
            return croisements;
          }
        }
      }
    }

    return croisements;
  }

  /**
   * Vérifie rapidement si les bounding boxes de deux ensembles de points se recoupent
   */
  private boundingBoxesSeRecoupent(points1: Point3D[], points2: Point3D[]): boolean {
    if (points1.length === 0 || points2.length === 0) return false;
    
    // Calcul des bounding boxes
    const bbox1 = this.calculerBoundingBox(points1);
    const bbox2 = this.calculerBoundingBox(points2);
    
    // Vérification de recouvrement avec tolérance
    const tolerance = this.toleranceDistance;
    return (
      bbox1.maxX + tolerance >= bbox2.minX && bbox1.minX - tolerance <= bbox2.maxX &&
      bbox1.maxZ + tolerance >= bbox2.minZ && bbox1.minZ - tolerance <= bbox2.maxZ
    );
  }

  /**
   * Calcule la bounding box d'un ensemble de points
   */
  private calculerBoundingBox(points: Point3D[]): {
    minX: number; maxX: number; minZ: number; maxZ: number;
  } {
    let minX = points[0].x, maxX = points[0].x;
    let minZ = points[0].z, maxZ = points[0].z;
    
    for (const point of points) {
      if (point.x < minX) minX = point.x;
      if (point.x > maxX) maxX = point.x;
      if (point.z < minZ) minZ = point.z;
      if (point.z > maxZ) maxZ = point.z;
    }
    
    return { minX, maxX, minZ, maxZ };
  }

  /**
   * Calcule la distance 3D entre deux points
   */
  private calculerDistance(p1: Point3D, p2: Point3D): number {
    return Math.sqrt(
      Math.pow(p1.x - p2.x, 2) +
      Math.pow(p1.y - p2.y, 2) +
      Math.pow(p1.z - p2.z, 2)
    );
  }

  /**
   * Calcule la distance 2D entre deux points (ignore Y)
   */
  private calculerDistance2D(p1: Point3D, p2: Point3D): number {
    return Math.sqrt(
      Math.pow(p1.x - p2.x, 2) +
      Math.pow(p1.z - p2.z, 2)
    );
  }

  /**
   * Calcule les statistiques des soudures
   */
  private calculerStatistiques(soudures: Soudure[]): {
    total: number;
    parType: Record<TypeSoudure, number>;
  } {
    const parType: Record<TypeSoudure, number> = {
      [TypeSoudure.MONTANT_POUTRE]: 0,
      [TypeSoudure.TRAVERSE_MONTANT]: 0,
      [TypeSoudure.POUTRE_POUTRE]: 0,
      [TypeSoudure.TRAVERSE_TRAVERSE]: 0,
      [TypeSoudure.DOUBLE_POUTRE]: 0,
      [TypeSoudure.ACCESSOIRE]: 0
    };

    soudures.forEach(soudure => {
      parType[soudure.type]++;
    });

    return {
      total: soudures.length,
      parType
    };
  }

  /**
   * Calcule le point de contact exact entre une traverse et un montant
   * Projette le point de la traverse sur la surface du montant cylindrique
   */
  private calculerPointContactExact(
    pointTraverse: Point3D,
    montant: { id: string; position: Point3D }
  ): Point3D {
    const rayonMontant = 0.025; // Rayon du montant (2.5cm)
    
    // Calculer la direction du point de traverse vers le centre du montant
    const dx = pointTraverse.x - montant.position.x;
    const dz = pointTraverse.z - montant.position.z;
    const distance2D = Math.sqrt(dx * dx + dz * dz);
    
    if (distance2D < 0.001) {
      // Point exactement au centre du montant - placer sur le bord
      return {
        x: montant.position.x + rayonMontant,
        y: pointTraverse.y,
        z: montant.position.z
      };
    }
    
    // Projeter le point sur la surface du montant
    const facteur = rayonMontant / distance2D;
    return {
      x: montant.position.x + dx * facteur,
      y: pointTraverse.y,
      z: montant.position.z + dz * facteur
    };
  }

  /**
   * Calcule les soudures de la double poutre
   * Soudures entre montants et poutres de la double poutre
   */
  private calculerSouduresDoublePoutre(
    doublePoutre: {
      montants: Array<{ id: string; position: Point3D; hauteur: number }>;
      poutres: Array<{ id: string; debut: Point3D; fin: Point3D }>;
    },
    parametres: ParametresSoudures,
    startId: number
  ): Soudure[] {
    const soudures: Soudure[] = [];
    let id = startId;

    // Soudures montant-poutre pour la double poutre
    doublePoutre.poutres.forEach(poutre => {
      doublePoutre.montants.forEach(montant => {
        // Vérifier si le montant est proche du début ou de la fin de la poutre
          const distanceDebut = this.calculerDistance(montant.position, poutre.debut);
          const distanceFin = this.calculerDistance(montant.position, poutre.fin);
         
         if (distanceDebut <= this.toleranceDistance) {
           soudures.push({
             id: `soudure_dp_${id++}`,
             type: TypeSoudure.DOUBLE_POUTRE,
             position: poutre.debut,
             taille: parametres.taille * parametres.tailleDoublePoutre,
             couleur: parametres.couleur,
             elementA: montant.id,
             elementB: poutre.id,
             visible: true
           });
         }
         
         if (distanceFin <= this.toleranceDistance) {
           soudures.push({
             id: `soudure_dp_${id++}`,
             type: TypeSoudure.DOUBLE_POUTRE,
             position: poutre.fin,
             taille: parametres.taille * parametres.tailleDoublePoutre,
             couleur: parametres.couleur,
             elementA: montant.id,
             elementB: poutre.id,
             visible: true
           });
         }
      });
    });


    return soudures;
  }

  /**
   * Met à jour la tolérance de distance pour la détection des connexions
   */
  public setToleranceDistance(tolerance: number): void {
    this.toleranceDistance = tolerance;
  }
}